import React, { useState, useMemo } from 'react';
import { useData } from '../context/DataContext';
import { db, STORES } from '../db/database';

/* ============================================================
   Aides de formatage (les anciennes dates peuvent être des
   numéros de série Excel, les nouvelles des chaînes ISO)
   ============================================================ */
const MS_PER_DAY = 86400000;
const EXCEL_OFFSET_MS = 25569 * MS_PER_DAY;

const dateToMS = (v) => {
  if (typeof v === 'number') return v * MS_PER_DAY - EXCEL_OFFSET_MS;
  const s = String(v || '');
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return Date.UTC(+m[1], +m[2] - 1, +m[3]);
  const d = new Date(s);
  return isNaN(d.getTime()) ? -Infinity : d.getTime();
};

const monthIndex = (v) => {
  const s = String(v || '');
  const m = s.match(/^\d{4}-(\d{2})/);
  if (m) return parseInt(m[1], 10) - 1;
  if (typeof v === 'number') {
    const d = new Date(dateToMS(v));
    return d.getUTCMonth();
  }
  const d = new Date(s);
  return isNaN(d.getTime()) ? -1 : d.getMonth();
};

const formatDate = (v) => {
  if (v === undefined || v === null || v === '') return '—';
  if (typeof v === 'number') {
    const d = new Date(dateToMS(v));
    const dd = String(d.getUTCDate()).padStart(2, '0');
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    return `${dd}/${mm}/${d.getUTCFullYear()}`;
  }
  const s = String(v);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  return s;
};

const euro = (n) =>
  (Number(n) || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

const MOIS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];

const Couts = () => {
  const { couts, chantiers, refreshData } = useData();

  const [form, setForm] = useState({
    date: new Date().toISOString().split('T')[0],
    fournisseur: '',
    chantier: '',
    client: '',
    libelle: '',
    montantHT: '',
    tauxTVA: '20',
    paye: false
  });

  /* --- Valeurs calculées en direct --- */
  const montantHT = parseFloat(form.montantHT) || 0;
  const tauxTVA = parseFloat(form.tauxTVA) || 0;
  const tvaDeductibleCalc = montantHT * tauxTVA / 100;
  const montantTTCcalc = montantHT + tvaDeductibleCalc;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  // Choix du chantier dans la liste → client pré-rempli automatiquement
  const handleChantierChange = (e) => {
    const chantier = e.target.value;
    const found = chantiers.find(ch => ch.chantier === chantier);
    setForm(prev => ({
      ...prev,
      chantier,
      client: found ? found.client : prev.client
    }));
  };

  const resetForm = () => {
    setForm({
      date: new Date().toISOString().split('T')[0],
      fournisseur: '',
      chantier: '',
      client: '',
      libelle: '',
      montantHT: '',
      tauxTVA: '20',
      paye: false
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.date || !form.fournisseur || !form.chantier || !form.montantHT) {
      alert('Veuillez remplir : Date, Fournisseur, Chantier et Montant HT.');
      return;
    }
    try {
      const coutToSave = {
        ...form,
        montantHT,
        tva: tauxTVA / 100,           // taux stocké en décimal (0.2 = 20 %)
        montantTTC: montantTTCcalc,
      };
      await db.add(STORES.COUTS, coutToSave);
      await refreshData();
      resetForm();
    } catch (error) {
      console.error('Erreur ajout coût:', error);
      alert('Erreur lors de l\'ajout du coût');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Supprimer ce coût ?')) {
      try {
        await db.delete(STORES.COUTS, id);
        await refreshData();
      } catch (error) {
        console.error('Erreur suppression:', error);
      }
    }
  };

  /* --- Lignes triées par date décroissante --- */
  const lignes = useMemo(
    () =>
      (couts || [])
        .map(c => ({ ...c, _ms: dateToMS(c.date) }))
        .sort((a, b) => b._ms - a._ms),
    [couts]
  );

  /* --- Synthèse globale --- */
  const synth = useMemo(() => {
    const totalHT = (couts || []).reduce((acc, c) => acc + (parseFloat(c.montantHT) || 0), 0);
    const totalTVA = (couts || []).reduce((acc, c) => acc + (parseFloat(c.montantHT) || 0) * (parseFloat(c.tva) || 0), 0);
    const totalTTC = totalHT + totalTVA;
    const payeTTC = (couts || [])
      .filter(c => c.paye)
      .reduce((acc, c) => acc + (parseFloat(c.montantHT) || 0) * (1 + (parseFloat(c.tva) || 0)), 0);
    return { totalHT, totalTVA, totalTTC, payeTTC, aPayerTTC: totalTTC - payeTTC };
  }, [couts]);

  /* --- Coûts par mois --- */
  const coutsParMois = useMemo(() => {
    return MOIS.map((mois, idx) => {
      const total = (couts || [])
        .filter(c => monthIndex(c.date) === idx)
        .reduce((acc, c) => acc + (parseFloat(c.montantHT) || 0), 0);
      return { mois, total };
    });
  }, [couts]);

  return (
    <div>
      <h2>💰 Coûts fournisseurs — achats de marchandises & sous-traitance</h2>
      <p className="info-text">
        <strong>Saisie manuelle</strong> — choisissez le chantier dans la liste. Le client est
        pré-rempli automatiquement. Ces coûts alimentent la marge brute par chantier / client,
        le CA et les coûts par mois, l'avance de trésorerie et la TVA collectée vs déductible.
      </p>

      <div className="cout-summary">
        <div className="cout-summary-item">
          <span>Total coûts HT</span>
          <strong>{euro(synth.totalHT)}</strong>
        </div>
        <div className="cout-summary-item">
          <span>TVA déductible</span>
          <strong>{euro(synth.totalTVA)}</strong>
        </div>
        <div className="cout-summary-item">
          <span>Total TTC</span>
          <strong>{euro(synth.totalTTC)}</strong>
        </div>
        <div className="cout-summary-item">
          <span>Payé (TTC)</span>
          <strong style={{ color: 'var(--color-success)' }}>{euro(synth.payeTTC)}</strong>
        </div>
        <div className="cout-summary-item">
          <span>À payer (TTC)</span>
          <strong style={{ color: synth.aPayerTTC > 0 ? 'var(--color-danger)' : 'inherit' }}>{euro(synth.aPayerTTC)}</strong>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="cout-form">
        <input
          name="date"
          type="date"
          value={form.date}
          onChange={handleChange}
          required
          aria-label="Date"
        />
        <input
          name="fournisseur"
          value={form.fournisseur}
          onChange={handleChange}
          placeholder="Fournisseur *"
          required
          aria-label="Fournisseur"
        />
        {chantiers && chantiers.length > 0 ? (
          <select
            name="chantier"
            value={form.chantier}
            onChange={handleChantierChange}
            required
            aria-label="Chantier (choisir dans la liste)"
          >
            <option value="" disabled>— Chantier * (liste) —</option>
            {chantiers.map(ch => (
              <option key={ch.chantier} value={ch.chantier}>{ch.chantier}</option>
            ))}
          </select>
        ) : (
          <input
            name="chantier"
            value={form.chantier}
            onChange={handleChange}
            placeholder="Chantier * (importez vos données d'abord)"
            required
            aria-label="Chantier"
          />
        )}
        <input
          name="client"
          value={form.client}
          onChange={handleChange}
          placeholder="Client (auto)"
          aria-label="Client"
        />
        <input
          name="libelle"
          value={form.libelle}
          onChange={handleChange}
          placeholder="Libellé (fournitures, main d'œuvre…)"
          aria-label="Libellé"
        />
        <input
          name="montantHT"
          type="number"
          min="0"
          step="0.01"
          value={form.montantHT}
          onChange={handleChange}
          placeholder="Montant HT *"
          required
          aria-label="Montant HT"
        />
        <label className="field-tva">
          Taux TVA
          <select
            name="tauxTVA"
            value={form.tauxTVA}
            onChange={handleChange}
            aria-label="Taux TVA"
          >
            <option value="20">20,0 %</option>
            <option value="10">10,0 %</option>
            <option value="5.5">5,5 %</option>
            <option value="2.1">2,1 %</option>
            <option value="0">0 %</option>
          </select>
        </label>
        <label className="field-paye">
          <input
            name="paye"
            type="checkbox"
            checked={form.paye}
            onChange={handleChange}
            aria-label="Payé ?"
          />
          Payé ?
        </label>
        <button type="submit">➕ Ajouter</button>
      </form>

      {montantHT > 0 && (
        <p className="calc-hint">
          TVA déductible : <strong>{euro(tvaDeductibleCalc)}</strong>
          &nbsp;&nbsp;·&nbsp;&nbsp;TTC : <strong>{euro(montantTTCcalc)}</strong>
        </p>
      )}

      <div className="table-scroll">
        <table className="data-table" role="table" aria-label="Liste des coûts fournisseurs">
        <thead>
          <tr>
            <th>Date</th>
            <th>Fournisseur</th>
            <th>Chantier</th>
            <th>Client</th>
            <th>Libellé</th>
            <th>Montant HT</th>
            <th>Taux TVA</th>
            <th>TVA déductible</th>
            <th>Montant TTC</th>
            <th>Payé ?</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {lignes.map(c => {
            const ht = parseFloat(c.montantHT) || 0;
            const taux = (parseFloat(c.tva) || 0) * 100;
            const tvaAmt = ht * (parseFloat(c.tva) || 0);
            const ttc = ht + tvaAmt;
            return (
              <tr key={c.id}>
                <td>{formatDate(c.date)}</td>
                <td>{c.fournisseur}</td>
                <td>{c.chantier}</td>
                <td>{c.client || '—'}</td>
                <td>{c.libelle || '—'}</td>
                <td>{euro(ht)}</td>
                <td>{taux.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} %</td>
                <td>{euro(tvaAmt)}</td>
                <td>{euro(ttc)}</td>
                <td>{c.paye ? '✅' : '❌'}</td>
                <td>
                  <button
                    className="btn-delete"
                    onClick={() => handleDelete(c.id)}
                    aria-label="Supprimer ce coût"
                  >
                    🗑️
                  </button>
                </td>
              </tr>
            );
          })}
          {lignes.length === 0 && (
            <tr>
              <td colSpan="11" style={{ textAlign: 'center', color: 'var(--color-text-secondary)', padding: '2rem' }}>
                Aucun coût enregistré — ajoutez votre premier achat ci-dessus.
              </td>
            </tr>
          )}
        </tbody>
      </table>
      </div>

      <h3 style={{ marginTop: '2rem' }}>📅 Coûts par mois</h3>
      <div className="cout-summary" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
        {coutsParMois.map((m, i) => (
          <div className="cout-summary-item" key={i}>
            <span>{m.mois}</span>
            <strong>{euro(m.total)}</strong>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Couts;